import {
  computed,
  ref,
  watch,
} from '@vue/composition-api'
import clearLocalSession from '@/auth/session'
import store from '@/store'
import router from '@/router'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'
import moment from 'moment'

moment.locale('ru')

export default function blockingAnAccessSession() {
  const refPrepareBlockingModal = ref(null)
  const refBlockingModal = ref(null)
  const isDebugMode = computed(() => store.state.app.settings.isDebugMode || false)
  const windowEvents = ref([
    'load',
  ])
  const documentEvents = ref([
    'mousemove',
    'mousedown',
    'touchstart',
    'touchmove',
    'click',
    'keypress',
    'keydown',
    'scroll',
    'wheel',
  ])
  const timeWaiting = computed(() => store.state?.app?.settings?.inactivity_time_for_blocking || 0)
  const timeInterval = computed(() => store.state?.app?.settings?.waiting_time_for_solution || 0)
  const onActivatedBlockingAnAccessSession = ref(false)
  const isEnabled = computed(() => onActivatedBlockingAnAccessSession.value && timeWaiting.value > 0 && timeInterval.value > 0 && timeInterval.value > 0)
  const currentTimeInterval = ref(timeInterval.value)
  const date = ref(moment([moment().year(), moment().month(), moment().date(), 0, 0, 0, 0]))
  const timeIntervalString = ref(null)
  const timerWaitingId = ref(0)
  const timerIntervalId = ref(0)

  function logout() {
    return store
      .dispatch('api/logout')
      .catch(error => {
        if (isDebugMode.value) {
          console.log('logout error', error)
        }
      })
      .finally(() => {
        clearLocalSession()
        ability.update(initialAbility)
        if (refPrepareBlockingModal.value) {
          refPrepareBlockingModal.value.hideModal()
        }

        // Redirect to login page (skip if already there to avoid NavigationDuplicated)
        if (router.currentRoute.name !== 'auth-login') {
          return router.push({ name: 'auth-login' }).catch(() => {})
        }
        return undefined
      })
  }

  const isWaitingTimerInterval = computed(() => currentTimeInterval.value >= 0)

  const destroyTimerInterval = () => {
    clearInterval(timerIntervalId.value)
    timerIntervalId.value = 0
  }

  const initTimerInterval = (time = 1000) => {
    currentTimeInterval.value = timeInterval.value
    timeIntervalString.value = date.value.clone().add(currentTimeInterval.value, 'seconds').format('mm:ss')
    timerIntervalId.value = setInterval(() => {
      if (isWaitingTimerInterval.value) {
        timeIntervalString.value = date.value.clone().add(currentTimeInterval.value, 'seconds').format('mm:ss')
        currentTimeInterval.value -= 1
      } else {
        destroyTimerInterval()
        logout()
      }
    }, time)
  }

  const destroyTimerWaiting = () => {
    clearTimeout(timerWaitingId.value)
    timerWaitingId.value = 0
    removeWindowEventListenersForTimerWaiting()
    removeDocumentEventListenerForTimerWaiting()
  }

  const showModal = () => {
    refBlockingModal.value.show()
    destroyTimerWaiting()
    initTimerInterval()
  }

  const hideModal = () => {
    refBlockingModal.value.hide()
    destroyTimerInterval()
    initTimerWaiting()
  }

  const initTimerWaiting = () => {
    addWindowEventListenersForTimerWaiting()
    addDocumentEventListenersForTimerWaiting()
    timerWaitingId.value = setTimeout(() => {
      showModal()
      destroyTimerWaiting()
    }, timeWaiting.value * 1000)
  }

  const resetTimerWaiting = () => {
    if (timerWaitingId.value > 0) {
      clearTimeout(timerWaitingId.value)
      initTimerWaiting()
    }
  }

  const addWindowEventListenerForTimerWaiting = eventName => {
    window.addEventListener(eventName, resetTimerWaiting, true)
  }

  const addWindowEventListenersForTimerWaiting = () => {
    windowEvents.value.forEach(eventName => addWindowEventListenerForTimerWaiting(eventName))
  }

  const removeWindowEventListenerForTimerWaiting = eventName => {
    window.removeEventListener(eventName, resetTimerWaiting, true)
  }

  const removeWindowEventListenersForTimerWaiting = () => {
    windowEvents.value.forEach(eventName => removeWindowEventListenerForTimerWaiting(eventName))
  }

  const addDocumentEventListenerForTimerWaiting = eventName => {
    document.addEventListener(eventName, resetTimerWaiting, true)
  }

  const addDocumentEventListenersForTimerWaiting = () => {
    documentEvents.value.forEach(eventName => addDocumentEventListenerForTimerWaiting(eventName))
  }

  const removeDocumentEventListenerForTimerWaiting = eventName => {
    document.removeEventListener(eventName, resetTimerWaiting, true)
  }

  const removeDocumentEventListenersForTimerWaiting = () => {
    documentEvents.value.forEach(eventName => removeDocumentEventListenerForTimerWaiting(eventName))
  }

  const onSubmit = () => {
    destroyTimerInterval()
    logout()
  }

  watch(isEnabled, value => {
    if (value) {
      initTimerWaiting()
    } else {
      destroyTimerWaiting()
    }
  })

  const provideVariables = {
    timeWaiting,
    timeInterval,
    onActivatedBlockingAnAccessSession,
    refPrepareBlockingModal,
    refBlockingModal,
    isDebugMode,
    timeIntervalString,
  }

  const provideFunctions = {
    initTimerWaiting,
    destroyTimerWaiting,
    logout,
    hideModal,
    showModal,
    onSubmit,
    addDocumentEventListenersForTimerWaiting,
    removeDocumentEventListenersForTimerWaiting,
    addWindowEventListenersForTimerWaiting,
    removeWindowEventListenersForTimerWaiting,
  }

  return {
    // Свойства
    ...provideVariables,
    // Методы
    ...provideFunctions,
  }
}
